import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {DelegationControllerAllEndpoint} from '@/types/endpoint-contracts'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {PageLayout} from '@/components/shared/PageLayout'
import {Pagination} from '@/components/ui/pagination'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {formatDateOnly} from '@/lib/date-utils'
import type {Delegation} from '@/types/delegation'
import {DelegationStatus, DelegationStatusBadge} from '@/types/delegation'
import {MutedText} from '@/components/shared/MutedText'

export function Delegations() {
    const i18n = useI18n()
    const services = useServices()
    const {hasPermission} = useAuth()
    const {workflowSteps} = useAppConfig()
    const canManage = hasPermission(permissions.delegations.admin)
    usePageTitle(i18n.delegations.title)


    const [revokeTarget, setRevokeTarget] = useState<Delegation | null>(null)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: DelegationControllerAllEndpoint,
        sortable: true,
        defaultSortBy: DelegationControllerAllEndpoint.sort.CREATED_AT,
        defaultPageSize: 20,
    })

    const delegationsQuery = useQuery({
        queryKey: ['admin-delegations', queryString, page, pageSize],
        queryFn: () => services.delegations.adminList(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const revokeMutation = useEntityMutation({
        mutationFn: (id: string) => services.delegations.adminRevoke(id),
        invalidate: ['admin-delegations'],
        successMessage: i18n.delegations.revokeSuccess,
        closeDialog: () => setRevokeTarget(null),
    })

    const delegations = delegationsQuery.data?.content ?? []
    const totalElements = delegationsQuery.data?.totalElements ?? 0
    const totalPages = delegationsQuery.data?.totalPages ?? 0

    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.delegation}
                title={i18n.delegations.allDelegations}
                count={totalElements}
                description={totalElements > 0 ? i18n.delegations.delegationsCount({count: totalElements}) : undefined}
                data-testid="delegations-list"
                filters={
                    <UnifiedSearchBar
                        contract={DelegationControllerAllEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.delegations.searchPlaceholder as string}
                    />
                }
                pagination={totalElements > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalElements}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {delegations.length === 0 && !delegationsQuery.isLoading && (
                    <MutedText block centered className="py-4"><MutedText>{i18n.delegations.noDelegations}</MutedText></MutedText>
                )}
                {delegations.length > 0 && (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-delegator">{i18n.delegations.columns.delegator}</TableHead>
                                <TableHead data-testid="col-deputy">{i18n.delegations.columns.deputy}</TableHead>
                                <TableHead data-testid="col-workflow-step">{i18n.delegations.columns.workflowStep}</TableHead>
                                <TableHead data-testid="col-status">{i18n.delegations.columns.status}</TableHead>
                                <TableHead data-testid="col-start-at">{i18n.delegations.columns.startAt}</TableHead>
                                <TableHead data-testid="col-end-at">{i18n.delegations.columns.endAt}</TableHead>
                                <TableHead data-testid="col-reason">{i18n.delegations.columns.reason}</TableHead>
                                {canManage && <TableHead data-testid="col-actions"/>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {delegations.map((d) => (
                                <TableRow key={d.id} data-testid={`delegation-row-${d.id}`}>
                                    <TableCell>
                                        <Strong>{d.delegator.displayName}</Strong>
                                    </TableCell>
                                    <TableCell>{d.deputy.displayName}</TableCell>
                                    <TableCell>{labelOf(workflowSteps, d.workflowStepCode)}</TableCell>
                                    <TableCell>
                                        <Badge variant={DelegationStatusBadge.of(d.status)?.variant ?? BadgeVariant.OUTLINE}>
                                            {i18n.delegations.statuses[d.status]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{d.permanent ? <Badge variant="outline">{i18n.delegations.permanent}</Badge> : formatDateOnly(d.startAt)}</TableCell>
                                    <TableCell>{d.permanent ? '\u2014' : formatDateOnly(d.endAt)}</TableCell>
                                    <TableCell>{d.reason ?? '\u2014'}</TableCell>
                                    {canManage && (
                                        <TableCell>
                                            {(d.status === DelegationStatus.SCHEDULED || d.status === DelegationStatus.ACTIVE) && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setRevokeTarget(d)}
                                                    data-testid={`admin-revoke-${d.id}`}
                                                >
                                                    {i18n.delegations.revokeDelegation}
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </PageLayout>

            {/* Admin Revoke Dialog */}
            <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.delegations.revokeDelegation}</DialogTitle>
                        <DialogDescription>{i18n.delegations.revokeConfirmation}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="admin-revoke"
                        onCancel={() => setRevokeTarget(null)}
                        saving={revokeMutation.isPending}
                        saveLabel={i18n.delegations.revokeDelegation}
                        destructive
                        saveType="button"
                        onSave={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
